/**
 * 💬 «گفتگوها» — پروندهٔ هر نفر در هر گروه.
 *
 * تب پیش‌نویس‌ها «کار روزانه» است (چه رفت و چه نرفت)، ولی این‌جا «رابطه» را نشان
 * می‌دهد: کل تاریخچهٔ او ↔ ما، آنچه دربارهٔ او فهمیده‌ایم، اینکه کازماشاپ معرفی شده
 * یا نه، و چه محصولی پیشنهاد شده. دو ستون، مثل هر پیام‌رسانی.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VerifiedIcon from '@mui/icons-material/Verified';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import userbotService, { UserbotLead, UserbotLeadDetail } from '../../services/userbotService';
import {
  Bubble, EmptyHint, INTENT_FA, IconAction, Insights, Kpis, LEAD_STAGE_META, Loading, Pill, SectionCard,
  Tag, UB, dayLabel, errMsg, fa, hueOf, initial, relTime,
} from './kit';
import type { Tone } from './kit';

const stageOf = (k: string) => LEAD_STAGE_META[k] || LEAD_STAGE_META.new;

// ────────────────────────────── گفتگو ──────────────────────────────

const ThreadView: React.FC<{ detail: UserbotLeadDetail; onSaved: () => void }> = ({ detail, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { lead } = detail;
  const [note, setNote] = useState(lead.note || '');
  const [stage, setStage] = useState(lead.stage);
  const [saving, setSaving] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    setNote(lead.note || '');
    setStage(lead.stage);
  }, [lead._id, lead.note, lead.stage]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [lead._id, lead.thread?.length]);

  const save = async () => {
    setSaving(true);
    try {
      await userbotService.updateLead(lead._id, { note, stage });
      enqueueSnackbar('ذخیره شد', { variant: 'success' });
      onSaved();
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const profile = lead.profile || ({} as any);
  const chips = [profile.object, profile.brand, profile.concern, profile.skinType, profile.budget].filter(Boolean) as string[];
  const st = stageOf(lead.stage);
  let lastDay = '';

  return (
    <div className="fac-card" style={{ margin: 0, display: 'flex', flexDirection: 'column', minHeight: 460 }}>
      {/* سربرگ */}
      <div className="fac-card-head" style={{ alignItems: 'flex-start' }}>
        <span className="ubc-avatar" style={{ background: hueOf(lead.senderId) }}>{initial(lead.senderName)}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {lead.senderName || 'بدون نام'}
            {lead.senderUsername && <span className="fac-muted" style={{ direction: 'ltr', fontWeight: 500 }}>@{lead.senderUsername}</span>}
            <Pill label={st.label} color={st.color} />
            {lead.siteMentioned && (
              <Tooltip title={`کازماشاپ معرفی شده${lead.siteMentionedAt ? ` — ${relTime(lead.siteMentionedAt)}` : ''}`} arrow>
                <VerifiedIcon style={{ color: UB.accent, fontSize: 17 }} />
              </Tooltip>
            )}
          </h3>
          <p style={{ flexBasis: 'auto' }}>
            {lead.chatId?.startsWith('dm:') ? 'پیام خصوصی (دایرکت)' : lead.chatTitle || lead.chatId} · {fa(lead.theirMessages)} پیام او · {fa(lead.ourReplies)} جواب ما
            {lead.accountId?.label ? ` · با حساب ${lead.accountId.label}` : ''}
          </p>
        </div>
        {(chips.length > 0 || (profile.intents || []).length > 0) && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flexBasis: '100%' }}>
            {(profile.intents || []).slice(0, 2).map((i: string) => <Tag key={i} color="secondary" label={INTENT_FA[i] || i} />)}
            {chips.map((c) => <Tag key={c} label={c} />)}
          </div>
        )}
      </div>

      {/* پیام‌ها */}
      <div className="ubc-scroll" style={{ flex: 1, padding: 14 }}>
        {(lead.thread || []).length === 0 && <EmptyHint icon={<ForumRoundedIcon />} title="هنوز پیامی ثبت نشده" />}
        {(lead.thread || []).map((t, i) => {
          const d = dayLabel(t.at);
          const showDay = d !== lastDay;
          lastDay = d;
          return (
            <React.Fragment key={i}>
              {showDay && (
                <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                  <span className="fac-tag gray">{fa(d)}</span>
                </div>
              )}
              <Bubble
                ours={t.role === 'us'}
                text={t.text}
                at={t.at}
                foot={
                  <>
                    {t.mentionedSite && <Tag color="secondary" label="کازماشاپ" />}
                    {(t.suggestedProducts || []).map((p) => (
                      <span key={p} className="fac-tag gray" title="محصول واقعی که به مدل داده شد" style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <StorefrontIcon style={{ fontSize: 12 }} />{p}
                      </span>
                    ))}
                  </>
                }
              />
            </React.Fragment>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* یادداشت و مرحله */}
      <div style={{ padding: 12, borderTop: '1px solid var(--color-border-light)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>مرحله</InputLabel>
          <Select label="مرحله" value={stage} onChange={(e) => setStage(e.target.value as any)}>
            {Object.entries(LEAD_STAGE_META).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" placeholder="یادداشت دربارهٔ این نفر — در جواب‌های بعدی لحاظ می‌شود…" value={note} onChange={(e) => setNote(e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
        <Button variant="contained" size="small" startIcon={<SaveIcon />} disabled={saving} onClick={save}>ذخیره</Button>
      </div>
    </div>
  );
};

// ────────────────────────────── تب ──────────────────────────────

const UserbotConversations: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<UserbotLead[]>([]);
  const [byStage, setByStage] = useState<Record<string, number>>({});
  const [stage, setStage] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<UserbotLeadDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await userbotService.leads({ stage, q, limit: 60 });
      setItems(r.items);
      setByStage(r.byStage || {});
      setSelected((cur) => cur || (r.items[0]?._id ?? null));
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [stage, q, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try { setDetail(await userbotService.lead(id)); }
    catch (e) { enqueueSnackbar(errMsg(e), { variant: 'error' }); }
    finally { setDetailLoading(false); }
  }, [enqueueSnackbar]);
  useEffect(() => { if (selected) loadDetail(selected); }, [selected, loadDetail]);

  const stages = useMemo(
    () => [{ key: 'all', label: 'همه' }, ...Object.entries(LEAD_STAGE_META).map(([k, v]) => ({ key: k, label: v.label }))],
    [],
  );
  const totalLeads = Object.values(byStage).reduce((s, n) => s + n, 0);
  const insights = useMemo(() => {
    const out: Array<{ tone: Tone; text: React.ReactNode }> = [];
    if (loading || !totalLeads) return out;
    const introduced = (byStage.introduced || 0) + (byStage.interested || 0);
    out.push({ tone: 'info', text: <>با <b>{fa(totalLeads)}</b> نفر گفتگو شکل گرفته؛ در <b>{fa(introduced)}</b> مورد کازماشاپ معرفی شده و <b>{fa(byStage.interested || 0)}</b> نفر بعد از معرفی پیگیر شدند.</> });
    if ((byStage.cold || 0) > totalLeads * 0.4) out.push({ tone: 'warn', text: <>{fa(byStage.cold || 0)} گفتگو سرد شده (جواب دادیم ولی ادامه نداد). اگر این نسبت بالاست، لحن یا زمان جواب‌ها را در تنظیمات بازبینی کنید.</> });
    return out;
  }, [byStage, totalLeads, loading]);

  return (
    <>
      <Insights items={insights} />
      {totalLeads > 0 && (
        <Kpis items={[
          { label: 'کل گفتگوها', value: totalLeads, count: true, color: 'blue', help: 'هر نفر در هر گروه یک پرونده دارد' },
          { label: 'در حال گفتگو', value: byStage.talking || 0, count: true, color: 'orange' },
          { label: 'کازماشاپ معرفی شد', value: byStage.introduced || 0, count: true, color: 'purple', help: 'در این گفتگوها نام سایت گفته شده' },
          { label: 'پیگیر شد', value: byStage.interested || 0, count: true, color: 'green', help: 'بعد از معرفی، خودش ادامه داد — نزدیک‌ترین‌ها به خرید' },
        ]} />
      )}
      <SectionCard
        flush
        icon={<ForumRoundedIcon />}
        title="گفتگوها"
        hint="تاریخچهٔ هر گفتگو به مدل داده می‌شود تا جواب‌ها ادامهٔ هم باشند؛ کازماشاپ از جواب دوم به بعد معرفی می‌شود، نه در پیام اول"
        actions={<IconAction title="به‌روزرسانی" onClick={load} disabled={loading}><RefreshIcon /></IconAction>}
      >
        <div style={{ padding: '12px 14px 0', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="جست‌وجو در نام، گروه و متن پیام‌ها…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 260, flex: '0 1 320px' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <div className="fac-quick">
            {stages.map((s) => (
              <button key={s.key} className={stage === s.key ? 'on' : ''} onClick={() => setStage(s.key)}>
                {s.key === 'all' ? s.label : `${s.label}${byStage[s.key] ? ` (${fa(byStage[s.key])})` : ''}`}
              </button>
            ))}
          </div>
        </div>

        <div className="ubc-split" style={{ padding: 14 }}>
          {/* فهرست */}
          <div className="fac-card ubc-scroll" style={{ margin: 0 }}>
            {loading && items.length === 0 && <Loading size={22} />}
            {!loading && items.length === 0 && (
              <EmptyHint icon={<PersonSearchRoundedIcon />} title="گفتگویی نیست" hint="وقتی کسی در گروه‌های فعال پیام مرتبط بفرستد، پرونده‌اش این‌جا ساخته می‌شود." />
            )}
            {items.map((l) => {
              const st = stageOf(l.stage);
              const last = l.thread?.[l.thread.length - 1];
              return (
                <div key={l._id} className={`ubc-pick ${selected === l._id ? 'on' : ''}`} onClick={() => setSelected(l._id)}>
                  <span className="ubc-avatar sm" style={{ background: hueOf(l.senderId) }}>{initial(l.senderName)}</span>
                  <div className="ubc-pick-main">
                    <b>{l.senderName || 'بدون نام'}</b>
                    <small>{l.chatId?.startsWith('dm:') ? 'پیام خصوصی' : l.chatTitle || l.chatId}</small>
                    {last && <small style={{ color: last.role === 'us' ? 'var(--color-primary)' : undefined }}>{last.role === 'us' ? '↩︎ ' : ''}{last.text}</small>}
                    <span style={{ display: 'flex', gap: 4, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Pill label={st.label} color={st.color} />
                      <small className="fac-muted">{fa(l.theirMessages)} پیام · {fa(l.ourReplies)} جواب</small>
                    </span>
                  </div>
                  <small className="fac-muted" style={{ whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>{relTime(l.lastMessageAt)}</small>
                </div>
              );
            })}
          </div>

          {/* گفتگو */}
          <div>
            {detailLoading && !detail && <div className="fac-card" style={{ margin: 0 }}><Loading /></div>}
            {!detailLoading && !detail && <div className="fac-card" style={{ margin: 0 }}><EmptyHint icon={<ForumRoundedIcon />} title="یک گفتگو را از فهرست انتخاب کنید" /></div>}
            {detail && <ThreadView detail={detail} onSaved={() => { load(); if (selected) loadDetail(selected); }} />}
          </div>
        </div>
      </SectionCard>
    </>
  );
};

export default UserbotConversations;
